import React, {useState} from 'react'
import {FormattedDate} from 'react-intl'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {ChildPolicy, MealPlan} from '@findhotel/sapi'
import {getUserCountryCode} from 'modules/meta/selectors'
import {getOptimizedRoomsConfig} from 'modules/sapiSearch/selectors'
import {getFAQData} from 'modules/searchApi/selectors'

import {ButtonLink} from '@vio/atlas/components/designSystem/ButtonLink'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Image} from '@vio/atlas/Image'
import noImage from '@vio/atlas/images/placeholder-room-image.svg'
import {Tag} from '@vio/atlas/src/components/designSystem/Tag'
import {linariaMq} from '@vio/atlas/src/utils/breakpoints'
import {imageProvider} from '@vio/atlas/testutils/imageProvider'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {getIsBrandVio} from '@vio/core/src/_web/brand/modules/selectors'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {roomsStringToRoomsSplit} from '@vio/core/src/room/business/roomConfiguration'
import {RoomWithoutOffers} from '@vio/core/src/room/types/room'
import {Meal} from '@vio/shared/src/accommodation/OfferPerks/Meal'
import {RoomDetailsOverlay} from '@vio/shared/src/accommodation/RoomDetailsOverlay'
import {RoomInfo} from '@vio/shared/src/accommodation/RoomInfo'
import {MirroredIcon} from '@vio/shared/src/booking/MirroredIcon'

const IMAGE_SIZE_MOBILE = 64
const IMAGE_SIZE_DESKTOP = 198
interface RoomDetailsInfoProps {
  childPolicy?: ChildPolicy
  hotelId?: string
  room: RoomWithoutOffers
  date: string
  midDate: string
  isCheckInDateAvailable?: boolean
  placement?: string
  showImage?: boolean
  mealPlan?: MealPlan[]
}

const ImageWrapper = styled.div<{isMobile: boolean}>`
  width: ${IMAGE_SIZE_MOBILE}px;
  height: ${IMAGE_SIZE_MOBILE}px;
  border-radius: ${cssTheme.layout.radius.md};
  overflow: hidden;
  min-height: auto;
  margin-block: ${cssTheme.layout.spacing.s400};
  margin-inline-start: ${cssTheme.layout.spacing.s400};
  margin-inline-end: 0;
  &:hover {
    cursor: pointer;
  }

  ${linariaMq.desktopXs} {
    width: ${IMAGE_SIZE_DESKTOP}px;
    aspect-ratio: 1;
    border-start-start-radius: ${cssTheme.layout.radius.md};
    border-start-end-radius: 0;
    border-end-start-radius: ${cssTheme.layout.radius.md};
    border-end-end-radius: 0;
    overflow: hidden;
    min-height: 130px;
    margin-block: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;
    &:hover {
      cursor: pointer;
    }
  }
`

const RoomDetailsInfoWrapper = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  flex-basis: 100%;
  border: 1px solid ${cssTheme.colors.border.neutral.secondary};
  border-radius: ${cssTheme.layout.radius.lg};
`

const RoomName = styled(Text)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
`

const NoImagePlaceholder = styled.div<{isBrandVio: boolean}>`
  width: ${IMAGE_SIZE_MOBILE}px;
  height: ${IMAGE_SIZE_MOBILE}px;
  ${({isBrandVio}) => (isBrandVio ? '' : 'filter: grayscale(100%);')}
  border-radius: ${cssTheme.layout.radius.md};
  min-height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url(${noImage.src});
  background-size: cover;
  background-position: center;

  ${linariaMq.desktopXs} {
    width: ${IMAGE_SIZE_DESKTOP}px;
    aspect-ratio: 1;
    border-start-start-radius: ${cssTheme.layout.radius.md};
    border-start-end-radius: 0;
    border-end-end-radius: 0;
    border-end-start-radius: ${cssTheme.layout.radius.md};
    min-height: 130px;
  }
`

const CheckInOutDateTag = ({
  date,
  midDate,
  isCheckInDateAvailable,
  placement
}: {
  date: string
  midDate: string
  isCheckInDateAvailable: boolean
  placement: string
}) => {
  return (
    <Tag variant="neutral" size="sm">
      <Box display="flex" alignItems="center" justifyContent="center">
        <Text
          variant="labelXS"
          as="span"
          dataId={`splitBooking.checkIn.${placement}`}
        >
          <FormattedDate
            value={isCheckInDateAvailable ? date : midDate}
            day="numeric"
            month="short"
            timeZone="UTC"
          />
        </Text>
        <Box paddingRight="s100" paddingLeft="s100">
          <MirroredIcon
            name="ArrowRight"
            size="xs"
            colorPath="content.neutral.secondary"
          />
        </Box>
        <Text
          variant="labelXS"
          tag="span"
          dataId={`splitBooking.checkOut.${placement}`}
        >
          <FormattedDate
            value={isCheckInDateAvailable ? midDate : date}
            day="numeric"
            month="short"
            timeZone="UTC"
          />
        </Text>
      </Box>
    </Tag>
  )
}

export const RoomDetailsInfo = ({
  childPolicy,
  hotelId,
  room,
  date,
  midDate,
  isCheckInDateAvailable = false,
  placement,
  showImage = true,
  mealPlan
}: RoomDetailsInfoProps) => {
  const {brand} = useBrand()
  const faqData = useSelector(state => getFAQData(state, hotelId))
  const roomsString = useSelector(state =>
    getOptimizedRoomsConfig(state, hotelId)
  )
  const isBrandVio = useSelector(getIsBrandVio)
  const roomsSplit = roomsStringToRoomsSplit(roomsString)
  const {isMobile} = useDeviceLayout()
  const [isRoomDetailsOverlayOpen, setIsRoomDetailsOverlayOpen] =
    useState(false)
  const userCountry = useSelector(getUserCountryCode)

  const openRoomDetailsOverlay = () => {
    setIsRoomDetailsOverlayOpen(true)
  }

  const enableRoomDetails =
    room &&
    (room?.images?.length > 0 ||
      room?.description?.length > 0 ||
      room?.amenities?.length > 0)

  return (
    <>
      <RoomDetailsInfoWrapper>
        {showImage && (
          <ImageWrapper isMobile={isMobile} onClick={openRoomDetailsOverlay}>
            {room.images.length === 0 ? (
              <NoImagePlaceholder isBrandVio={isBrandVio} />
            ) : (
              <Image url={room.images[0].url} />
            )}
          </ImageWrapper>
        )}
        <Box flex="1" paddingLeft="s400">
          <Box paddingTop="s400" paddingBottom="s400" paddingRight="s400">
            <Box
              paddingBottom="s250"
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <CheckInOutDateTag
                date={date}
                midDate={midDate}
                isCheckInDateAvailable={isCheckInDateAvailable}
                placement={placement}
              />
              {enableRoomDetails ? (
                <ButtonLink
                  dataId={`roomDetailsButton.${placement}`}
                  size="sm"
                  variant="primary"
                  isCompact
                  onClick={openRoomDetailsOverlay}
                >
                  <Text variant={isMobile ? 'labelXS' : 'labelS'}>
                    {isMobile ? (
                      <FormattedMessageWrapper
                        id="details"
                        defaultMessage="Details"
                        ddActionName="RoomDetailsButton"
                      />
                    ) : (
                      <FormattedMessageWrapper
                        id="roomDetails"
                        defaultMessage="Room details"
                        ddActionName="RoomDetailsButton"
                      />
                    )}
                  </Text>
                </ButtonLink>
              ) : null}
            </Box>
            <Box display="flex" flexDirection="column" gap="s200">
              <RoomName variant="titleM" colorPath="content.neutral.primary">
                {room.name}
              </RoomName>
              <RoomInfo
                room={room}
                userCountry={userCountry}
                roomsSplit={roomsSplit}
                displayTopAmenities={false}
                childPolicy={childPolicy}
              />
              {mealPlan && mealPlan.length > 0 && <Meal mealPlan={mealPlan} />}
            </Box>
          </Box>
        </Box>
      </RoomDetailsInfoWrapper>
      {enableRoomDetails && (
        <RoomDetailsOverlay
          isOpen={isRoomDetailsOverlayOpen}
          room={room}
          onClose={() => setIsRoomDetailsOverlayOpen(false)}
          imageProvider={imageProvider}
          userCountry={userCountry}
          brand={brand}
          childPolicy={childPolicy}
          faqData={faqData}
          roomsSplit={roomsSplit}
        />
      )}
    </>
  )
}
